import React, { useEffect, useState, useMemo, useCallback } from 'react';
import PostCard from '../components/Posts/PostCard';
import AdCard from '../components/AdCard/AdCard';
import { fetchPosts } from '../api';
import { useAuth } from '../contexts/AuthContext';
import { resolveImageUrl } from '../utils/imageUrl';
import './Blog.css';

const Blog = () => {
  const { canAccessExclusiveContent } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTag, setSelectedTag] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Memoize tag extraction to prevent unnecessary recalculations
  const allTags = useMemo(() => {
    return posts.reduce((tags, post) => {
      if (post.tags && Array.isArray(post.tags)) {
        post.tags.forEach(tag => {
          if (!tags.includes(tag)) tags.push(tag);
        });
      }
      return tags;
    }, []).sort();
  }, [posts]);

  // Memoize filtered posts to optimize performance
  const filteredPosts = useMemo(() => {
    return posts.filter(post => {
      const matchesSearch = !searchTerm || 
        post.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (post.excerpt && post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()));
      
      const matchesTag = !selectedTag || 
        (post.tags && post.tags.includes(selectedTag));

      return matchesSearch && matchesTag;
    });
  }, [posts, searchTerm, selectedTag]);

  // Memoize post categorization
  const postCategories = useMemo(() => {
    const stickyPosts = filteredPosts.filter(post => post.is_sticky);
    const regularPosts = filteredPosts.filter(post => !post.is_sticky);
    const featuredPosts = regularPosts.filter(post => post.is_featured);
    const standardPosts = regularPosts.filter(post => !post.is_featured);

    return { stickyPosts, regularPosts, featuredPosts, standardPosts };
  }, [filteredPosts]);

  // Utility function for image URL handling
  const getImageUrl = useCallback((imageUrl) => {
    return resolveImageUrl(imageUrl);
  }, []);

  useEffect(() => {
    let isMounted = true;
    
    const loadPosts = async () => {
      try {
        setLoading(true);
        setError(null); // Reset error state on new fetch
        const data = await fetchPosts();
        
        if (isMounted) {
          // Better data handling - check for various response formats
          const postsData = data?.data || data || [];
          if (Array.isArray(postsData)) {
            setPosts(postsData);
          } else {
            throw new Error('Invalid posts data format received');
          }
        }
      } catch (err) {
        console.error('Failed to load posts:', err);
        
        if (isMounted) {
          setError(err.message || 'Failed to load posts. Please try again.');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadPosts();

    // Cleanup function to prevent memory leaks
    return () => {
      isMounted = false;
    };
  }, []);

  const handleTagFilter = useCallback((tag) => {
    setSelectedTag(currentTag => currentTag === tag ? '' : tag);
  }, []);

  const clearFilters = useCallback(() => {
    setSelectedTag('');
    setSearchTerm('');
  }, []);

  const handleRetry = useCallback(() => {
    window.location.reload();
  }, []);

  // Memoize filter controls to prevent unnecessary re-renders
  const renderFilterControls = useCallback((variant = 'desktop') => (
    <div
      className={`blog-sidebar-section blog-page-controls ${
        variant === 'mobile' ? 'blog-page-controls-mobile' : 'blog-page-controls-desktop'
      }`}
    >
      {/* Search Section - moved to top for better UX */}
      <div className="blog-search-section">
        <h4>Search Posts</h4>
        <input
          type="text"
          placeholder="Search posts..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="blog-search-input"
          aria-label="Search posts"
        />
      </div>

      {/* Tags Section */}
      {allTags.length > 0 && (
        <div className="blog-tags-section">
          <h4>Filter by Topics</h4>
          <div className="blog-tags-list">
            {allTags.map(tag => (
              <button
                key={tag}
                onClick={() => handleTagFilter(tag)}
                className={`blog-tag-btn ${selectedTag === tag ? 'active' : ''}`}
                type="button"
                aria-label={`Filter posts by ${tag}`}
                aria-pressed={selectedTag === tag}
              >
                #{tag}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Clear Filters Button */}
      {(selectedTag || searchTerm) && (
        <button 
          onClick={clearFilters} 
          className="blog-clear-filters-btn" 
          type="button"
          aria-label="Clear all filters"
        >
          Clear Filters
        </button>
      )}
    </div>
  ), [allTags, selectedTag, searchTerm, handleTagFilter, clearFilters]);

  // Loading State Component
  const renderLoadingState = () => (
    <div className="blog-page">
      <div className="blog-container">
        <main className="blog-main">
          <div className="blog-header">
            <h1>It Guhso</h1>
          </div>
          <div className="blog-loading-state">
            <div className="blog-loading-spinner">
              <i className="fas fa-spinner fa-spin" aria-hidden="true"></i>
            </div>
          </div>
        </main>
      </div>
    </div>
  );

  // Error State Component
  const renderErrorState = () => (
    <div className="blog-page">
      <div className="blog-container">
        <main className="blog-main">
          <div className="blog-header">
            <h1>It Guhso</h1>
          </div>
          <div className="blog-error-state">
            <div className="error-icon">
              <i className="fas fa-exclamation-triangle" aria-hidden="true"></i>
            </div>
            <h3>Failed to Load Posts</h3>
            <p>{error}</p>
            <button 
              onClick={handleRetry} 
              className="blog-retry-btn"
              type="button"
            >
              <i className="fas fa-refresh" aria-hidden="true"></i>
              Try Again
            </button>
          </div>
        </main>
      </div>
    </div>
  );

  // No Posts Found Component
  const renderNoPostsFound = () => (
    <div className="blog-no-posts">
      <i className="fas fa-search" aria-hidden="true"></i>
      <h4>No posts found</h4>
      <p>
        {searchTerm && selectedTag 
          ? `No posts match "${searchTerm}" in #${selectedTag}`
          : searchTerm 
          ? `No posts match "${searchTerm}"`
          : selectedTag 
          ? `No posts found in #${selectedTag}`
          : 'No posts available'
        }
      </p>
      {(searchTerm || selectedTag) && (
        <button onClick={clearFilters} className="blog-clear-filters-btn">
          Show All Posts
        </button>
      )}
    </div>
  );

  if (loading) return renderLoadingState();
  if (error) return renderErrorState();

  const { stickyPosts, featuredPosts, standardPosts } = postCategories;

  return (
    <div className="blog-page">
      <div className="blog-container">
        {/* Main Content Area */}
        <main className="blog-main">
          {/* Blog Header */}
          <div className="blog-header">
            <h1>It Guhso</h1>
          </div>

          {/* Sticky Posts */}
          {stickyPosts.length > 0 && (
            <section className="sticky-posts-section">
              <h3 className="blog-section-title">
                <i className="fas fa-thumbtack" aria-hidden="true"></i>
                Pinned Posts
              </h3>
              <div className="sticky-posts-grid">
                {stickyPosts.map(post => (
                  <PostCard 
                    key={`sticky-${post.id}`} 
                    post={post} 
                    variant="sticky" 
                    className="blog-post-card"
                    canAccessExclusiveContent={canAccessExclusiveContent}
                  />
                ))}
              </div>
            </section>
          )}

          {/* Featured Posts - only show when no filters are active */}
          {featuredPosts.length > 0 && !selectedTag && !searchTerm && (
            <section className="featured-posts-section">
              <h3 className="blog-section-title">
                <i className="fas fa-star" aria-hidden="true"></i>
                Featured Stories
              </h3>
              <div className="featured-posts-grid">
                {featuredPosts.slice(0, 3).map(post => (
                  <PostCard 
                    key={`featured-${post.id}`} 
                    post={post} 
                    variant="featured" 
                    className="blog-post-card"
                    canAccessExclusiveContent={canAccessExclusiveContent}
                  />
                ))}
              </div>
            </section>
          )}

          {/* All Posts */}
          <section className="all-posts-section">
            <h3 className="blog-section-title">
              <i className="fas fa-newspaper" aria-hidden="true"></i>
              {selectedTag || searchTerm ? 'Filtered Posts' : 'Recent Posts'}
            </h3>
            
            {filteredPosts.length === 0 ? (
              renderNoPostsFound()
            ) : (
              <div className="posts-grid">
                {standardPosts.map((post, index) => (
                  <React.Fragment key={`post-${post.id}`}>
                    <PostCard post={post} className="blog-post-card" canAccessExclusiveContent={canAccessExclusiveContent} />
                    {/* Insert ad after every 4th post */}
                    {(index + 1) % 4 === 0 && (
                      <div className="inline-ad-container">
                        <AdCard key={`ad-${post.id}-${index}`} />
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            )}
          </section>
        </main>

        {/* Sidebar */}
        <aside className="blog-sidebar">
          {/* Search and Filters */}
          {renderFilterControls('desktop')}

          {/* Recent Posts Sidebar - only show if we have posts */}
          {posts.length > 0 && (
            <div className="blog-sidebar-section recent-posts-section">
              <h4>Recent Posts</h4>
              <div className="sidebar-posts">
                {posts.slice(0, 5).map(post => (
                  <div key={`sidebar-${post.id}`} className="sidebar-post">
                    {getImageUrl(post.cover_image_url || post.cover_image) && (
                      <img 
                        src={getImageUrl(post.cover_image_url || post.cover_image)}
                        alt={post.title || 'Post thumbnail'}
                        className="sidebar-post-image"
                        loading="lazy"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    )}
                    <div className="sidebar-post-content">
                      <h5>{post.title || 'Untitled Post'}</h5>
                      <p className="sidebar-post-date">
                        {new Date(post.published_at || post.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Popular Tags - only show if we have tags */}
          {allTags.length > 0 && (
            <div className="blog-sidebar-section popular-blog-tags-section">
              <h4>Popular Topics</h4>
              <div className="blog-popular-tags">
                {allTags.slice(0, 8).map(tag => (
                  <button
                    key={tag}
                    onClick={() => handleTagFilter(tag)}
                    className={`blog-popular-tag ${selectedTag === tag ? 'active' : ''}`}
                    aria-label={`Filter posts by ${tag}`}
                    aria-pressed={selectedTag === tag}
                  >
                    #{tag}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Advertisement */}
          <div className="blog-sidebar-section ad-section">
            <AdCard />
          </div>

          {/* Newsletter Signup */}
          <div className="blog-sidebar-section newsletter-section">
            <h4>Stay Updated</h4>
            <p>Get the latest posts delivered to your inbox</p>
            <form className="newsletter-form" onSubmit={(e) => e.preventDefault()}>
              <input 
                type="email" 
                placeholder="Enter your email" 
                className="newsletter-input"
                aria-label="Email address for newsletter subscription"
                required
              />
              <button className="newsletter-btn" type="submit">
                Subscribe
              </button>
            </form>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default Blog;
